import { useState, useEffect } from "react";
import axios from 'axios';
import {
  Checkbox,
  Tabs,
  Tab,
  Box,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Typography,
  TableRow,
  TableCell,
  Tooltip,
  FormControlLabel,
  Grid
} from "@mui/material";
import { BarChart, Bar, Cell, YAxis } from "recharts";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { getVal, setBool, getTableData } from "../../helpers/process_morningstar.js";
import { useNavigate } from "react-router-dom";
import { performanceColorCellFromValue } from "../utils/PerformanceColourScale.js"

const WEBAPI = process.env.REACT_APP_WEB_API;

function Comparables() {
  const [comparables, setComparables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate()

  useEffect(() => {
    const fetchComparables = async () => {
      try {
        const security = await getTableData("Security")
        const productId = security[0]['ProductID']
        setLoading(true);

        if (productId === undefined) {
          setError('Ticker is undefined');
          return;
        }

        const res = await axios.get(`${WEBAPI}/ticker/comparables/${productId}`);

        // if (!res.ok) {
        //   throw new Error('Failed to fetch comparables');
        // }

        const data = res.data
        for(let i = 0; i < data.length; i++){
          const cfirr = data[i].Chart_CFIRRex.split(',')
          data[i].Chart_CFIRRex = cfirr.map(Number)
        }

        setComparables(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchComparables();
  }, []);

  return (
    <Box sx={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "stretch",
        width: '70%',
        height: "100%",
        marginRight: 2,
      }}
    >
        <Typography sx={{
          fontWeight: 'bold',
          color: "#4e95d9"
        }}>
            Comparables
        </Typography>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid currentColor",
            mt: 2,
          }}
        >
          <Typography variant="body2">
            Stock Market %
          </Typography>
          <Typography variant="body2" sx={{ pr: 1 }}>
            24 Qtrs
          </Typography>
        </Box>
        <Grid 
          container 
          spacing={2}
          sx={{
            mt: 1,
          }}
          >
          <Grid item xs={8} sx={{ pb: 0, ml: -1 }}>
            <Box display="flex" alignItems="center" width="100%">
              <Box sx={{ 
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <Typography fontSize={'small'} sx={{ fontWeight: "bold" }}>
                  3Y
                </Typography>
              </Box>
              <Box sx={{ 
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <Typography fontSize={'small'} sx={{ fontWeight: "bold" }}>
                  1Y
                </Typography>
              </Box>
              <Box sx={{ 
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <Typography fontSize={'small'} sx={{ fontWeight: "bold" }}>
                  6M
                </Typography>
              </Box>
              <Box sx={{ 
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <Typography fontSize={'small'} sx={{ fontWeight: "bold" }}>
                  3M
                </Typography>
              </Box>
              <Box sx={{ 
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <Typography fontSize={'small'} sx={{ fontWeight: "bold" }}>
                  1M
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={4}>
            <Typography 
              fontSize={'small'}
              sx={{
                fontWeight: "bold",
                textAlign: "right",
                pr: 1,
                color: 'green'
            }} >
              CFIRR
            </Typography>
          </Grid>
        </Grid>
        {comparables.map((t) => (
          <TableRow sx={{ cursor: "pointer", "&:hover": { backgroundColor: "rgba(0, 0, 0, 0.15)" }, paddingTop: 1 }} onClick={() => navigate(`/${t.Ticker}`)}>
            <TableRow>
              <TableCell padding="none" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", width: "100px", minWidth: "80px" }}>
                {t.Ticker}
              </TableCell>
              <TableCell padding="none" sx={{ 
                fontFamily: "ArchivoNarrow-Regular", 
                fontSize: "0.75rem", 
                width: "50%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}>
                {t.Company}
              </TableCell>
              <TableCell padding="none" sx={{ fontSize: "0.5rem", paddingLeft: 1, width: "10%", textAlign: "center" }} align="center">
                <Tooltip title={t.DomicileCountryFlag}>
                  <img src={`https://flagcdn.com/w20/${t.DomicileCountryFlag?.toLowerCase()}.png`} width="20" alt="flag" />
                </Tooltip>
              </TableCell>
              <TableCell padding="none" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem", paddingLeft: 1, width: "60px", textAlign: "right" }}>
                {t.Sector}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell colSpan={5} padding="none">
                <Box display="flex" alignItems="center" width="100%">
                  <Box sx={{ 
                    fontFamily: "ArchivoNarrow-Regular", 
                    fontSize: "10px", 
                    backgroundColor: performanceColorCellFromValue(t.PriceChg3Years),
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.25,
                    py: 1
                    }}>
                    {t.PriceChg3Years}%
                  </Box>
                  <Box sx={{ 
                    fontFamily: "ArchivoNarrow-Regular", 
                    fontSize: "10px", 
                    backgroundColor: performanceColorCellFromValue(t.PriceChg1Years),
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.25,
                    py: 1
                    }}>
                    {t.PriceChg1Years}%
                  </Box>
                  <Box sx={{ 
                    fontFamily: "ArchivoNarrow-Regular", 
                    fontSize: "10px", 
                    backgroundColor: performanceColorCellFromValue(t.PriceChg6Months),
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.25,
                    py: 1 
                    }}>
                    {t.PriceChg6Months}%
                  </Box>
                  <Box sx={{ 
                    fontFamily: "ArchivoNarrow-Regular", 
                    fontSize: "10px", 
                    paddingLeft: 0.5, 
                    backgroundColor: performanceColorCellFromValue(t.PriceChg3Months),
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.25,
                    py: 1 
                    }}>
                    {t.PriceChg3Months}%
                  </Box>
                  <Box sx={{ 
                    fontFamily: "ArchivoNarrow-Regular", 
                    fontSize: "10px", 
                    paddingLeft: 0.5, 
                    backgroundColor: performanceColorCellFromValue(t.PriceChg1Months),
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 0.25,
                    py: 1
                    }}>
                    {t.PriceChg1Months}%
                  </Box>
                  <Box sx={{ width: 100, minWidth: 100, maxWidth: 100, paddingLeft: 1 }}>
                    {t.Sector === "FI" ? (
                      <Typography align="center" sx={{ fontFamily: "ArchivoNarrow-Regular", fontSize: "0.75rem" }}>
                        N/A
                      </Typography>
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <BarChart 
                          width={100} 
                          height={30} 
                          data={t.Chart_CFIRRex.map(value => ({ value }))} 
                          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
                        >
                          <YAxis type="number" domain={[-10, 30]} hide={true} allowDataOverflow={true} />
                          <Bar dataKey="value">
                            {t.Chart_CFIRRex.map((entry, index) => (
                              <Cell 
                                key={index} 
                                fill={entry >= 0 ? 'green' : 'red'}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </Box>
                    )}
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          </TableRow>
        ))}
    </Box>
  );
}

export default Comparables;
